import type { BaseTotal, CurrencyBalance } from '@familywallet/shared';
import { useTranslation } from 'react-i18next';
import { formatAmount, owedAmount } from '../lib/money';

/** One amount in its currency. On a card a negative balance reads "Owes 40". */
export function CurrencyAmount({
  amount,
  currency,
  owes = false,
}: {
  amount: string;
  currency: string;
  owes?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const owed = owes ? owedAmount(amount) : null;
  return (
    <span className="tabular-nums" dir="ltr">
      {owed
        ? t('accounts.owes', { amount: formatAmount(owed, currency, i18n.language) })
        : formatAmount(amount, currency, i18n.language)}
    </span>
  );
}

/** Each currency on its own line, base currency first (the API already sorts them). */
export function BalanceLines({
  balances,
  owes = false,
  className,
}: {
  balances: readonly CurrencyBalance[];
  owes?: boolean;
  className?: string;
}) {
  return (
    <span className={className ?? 'flex flex-col'}>
      {balances.map((b) => (
        <CurrencyAmount key={b.currency} amount={b.balance} currency={b.currency} owes={owes} />
      ))}
    </span>
  );
}

/**
 * "≈ 1,250.00 US$ at today's rate" under balances in several currencies. Says which currencies
 * have no family rate yet (they are left out of the estimate). Shows nothing when there is
 * nothing to estimate.
 */
export function BaseEstimate({ total, owes = false }: { total: BaseTotal; owes?: boolean }) {
  const { t, i18n } = useTranslation();
  if (!total.estimated && total.missingRates.length === 0) return null;
  const owed = owes ? owedAmount(total.amount) : null;
  const amount = formatAmount(owed ?? total.amount, total.currency, i18n.language);
  return (
    <span className="flex flex-col text-[13px] font-normal text-muted">
      {total.estimated && (
        <span>
          <span dir="ltr" className="tabular-nums">
            ≈ {owed ? t('accounts.owes', { amount }) : amount}
          </span>{' '}
          {t('rates.atTodaysRate')}
        </span>
      )}
      {total.missingRates.length > 0 && (
        <span>{t('rates.missingForEstimate', { currencies: total.missingRates.join(', ') })}</span>
      )}
    </span>
  );
}
